import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  DO_NOT_CALL_SOURCE_LABELS,
  DoNotCallAssistant,
  DoNotCallLead,
  DoNotCallListing,
  DoNotCallNumber,
  addListings,
  doNotCallService,
  leadName,
} from "@/services/doNotCallService";
import { Check, ChevronsUpDown, Loader2, PhoneOff, PlusCircle, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : "—");

// Wait this long after the last keystroke before searching the leads.
const LEAD_SEARCH_DELAY_MS = 250;

export default function DoNotCallTab() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [numbers, setNumbers] = useState<DoNotCallNumber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // Numbers are added by hand only from a lead (kmhari/voxi-frontend#103): the lead picked, and
  // the leads the search found.
  const [newLead, setNewLead] = useState<DoNotCallLead | null>(null);
  const [leadQuery, setLeadQuery] = useState("");
  const [leadResults, setLeadResults] = useState<DoNotCallLead[]>([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [leadSearchFailed, setLeadSearchFailed] = useState(false);
  const [isLeadPickerOpen, setIsLeadPickerOpen] = useState(false);
  // The dialog's element: the lead picker is portalled into it, else the dialog sits on top of the
  // picker and its focus trap takes the typing (see PopoverContent's container).
  const [addDialogEl, setAddDialogEl] = useState<HTMLDivElement | null>(null);
  const [newReason, setNewReason] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  // Block for the whole organization, or for the agents ticked (one or more, in one save).
  const [blockFor, setBlockFor] = useState<"organization" | "agents">("organization");
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [assistants, setAssistants] = useState<DoNotCallAssistant[]>([]);
  // Owners, admins and platform admins remove numbers (the table's RLS); members only add them
  const [canRemove, setCanRemove] = useState(false);

  useEffect(() => {
    if (organizationId) {
      fetchNumbers();
      doNotCallService.canRemove(organizationId).then(setCanRemove);
      doNotCallService.getAssistants(organizationId).then(setAssistants).catch(() => setAssistants([]));
    }
  }, [organizationId]);

  // Search the leads while the picker is open; only the latest search's answer is kept.
  useEffect(() => {
    if (!organizationId || !isLeadPickerOpen) return;
    let cancelled = false;
    setIsSearchingLeads(true);
    const timer = setTimeout(async () => {
      try {
        const found = await doNotCallService.searchLeads(organizationId, leadQuery);
        if (cancelled) return;
        setLeadResults(found);
        setLeadSearchFailed(false);
      } catch (error) {
        if (cancelled) return;
        console.error("Error searching leads:", error);
        setLeadResults([]);
        setLeadSearchFailed(true);
      } finally {
        if (!cancelled) setIsSearchingLeads(false);
      }
    }, LEAD_SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizationId, leadQuery, isLeadPickerOpen]);

  const resetAddForm = () => {
    setNewLead(null);
    setLeadQuery("");
    setNewReason("");
    setBlockFor("organization");
    setSelectedAgentIds([]);
  };

  // quiet: refresh without the loading state, which replaces the whole tab (and an open dialog)
  // with a spinner while the list is empty.
  const fetchNumbers = async ({ quiet = false } = {}) => {
    if (!organizationId) return;
    if (!quiet) setIsLoading(true);
    try {
      setNumbers(await doNotCallService.getNumbers(organizationId));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load the do-not-call list",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!organizationId || !newLead) return;
    setIsAdding(true);
    try {
      const { added, skipped, failed } = await doNotCallService.addLead(
        organizationId,
        newLead,
        newReason,
        blockFor === "organization" ? null : tickableAgentIds,
      );
      const number = added[0]?.phone_number ?? newLead.e164;
      const names = (ids: (string | null)[]) => ids.map(agentName).join(", ");
      const addedFor = added.map((row) => row.assistant_id);
      toast({
        title: failed.length ? "Number blocked for some agents" : "Number blocked",
        variant: failed.length ? "destructive" : "default",
        description:
          blockFor === "organization"
            ? `${newLead.name} (${number}) will not be called by this organization. Their pending calls were cancelled.`
            : `${newLead.name} (${number}) will not be called by ${names(addedFor)}. Their pending calls with ${
                addedFor.length === 1 ? "this agent" : "these agents"
              } were cancelled.${skipped.length ? ` Already blocked for ${names(skipped)}.` : ""}${
                failed.length ? ` Could not add it for ${names(failed)}; try again.` : ""
              }`,
      });
      setIsAddDialogOpen(false);
      resetAddForm();
      await fetchNumbers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add the number",
        variant: "destructive",
      });
      // Several agents are saved one by one when one of them was listed meanwhile: an error part
      // way leaves some saved, so the list (and which agents can still be ticked) is read again,
      // quietly: the dialog stays open on its error.
      await fetchNumbers({ quiet: true });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (row: DoNotCallNumber) => {
    try {
      await doNotCallService.removeNumber(row.id);
      toast({
        title: "Number unblocked",
        description: row.assistant
          ? `${row.phone_number} can be called again by ${row.assistant.name}.`
          : `${row.phone_number} can be called again by this organization.`,
      });
      await fetchNumbers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove the number",
        variant: "destructive",
      });
    }
  };

  const agentName = (id: string | null) => assistants.find((a) => a.id === id)?.name ?? "an agent";

  // How each number is already listed in this organization, to grey out a lead whose number is
  // already blocked for the scope picked (or for the whole organization, which covers every agent).
  const listedFor = useMemo(
    () => addListings(new Map<string, DoNotCallListing>(), numbers, (row) => row.phone_number),
    [numbers],
  );

  // Why a lead cannot be picked, or null when it can. A whole-organization listing covers every
  // agent, so nothing more can be added for that number.
  const leadProblem = (lead: DoNotCallLead): string | null => {
    if (!lead.e164) return "Not a valid phone number";
    if (listedFor.get(lead.e164)?.organization) return "Already blocked for the whole organization";
    return null;
  };
  const newLeadProblem = newLead ? leadProblem(newLead) : null;
  // The agents that already block the picked lead's number: shown, but cannot be ticked.
  const agentsListingNewLead = (newLead?.e164 && listedFor.get(newLead.e164)?.assistantIds) || new Set<string>();
  const tickableAgentIds = selectedAgentIds.filter((id) => !agentsListingNewLead.has(id));
  const toggleAgent = (id: string) =>
    setSelectedAgentIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const canAdd =
    !!newLead && !newLeadProblem && !isAdding && (blockFor === "organization" || tickableAgentIds.length > 0);

  if (isLoading && numbers.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Do-not-call list</CardTitle>
          <CardDescription>
            Numbers this organization, or one of its agents, never calls. A call to a listed number
            is refused before it is dialled, and its pending scheduled calls and retries are
            cancelled. When someone asks on a call not to be called again, the number is added for
            the agent that called them.
          </CardDescription>
        </div>
        <Dialog
          open={isAddDialogOpen}
          onOpenChange={(open) => {
            // Not while a save runs (Escape or a click outside): its result needs the dialog.
            if (!open && isAdding) return;
            setIsAddDialogOpen(open);
            if (!open) resetAddForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Add Number
            </Button>
          </DialogTrigger>
          <DialogContent ref={setAddDialogEl}>
            <DialogHeader>
              <DialogTitle>Add to the do-not-call list</DialogTitle>
              <DialogDescription>
                Pick one of this organization's leads. Its phone number is added; only a real
                phone number for its country can be.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="dnc-lead">Lead</Label>
                <Popover open={isLeadPickerOpen} onOpenChange={setIsLeadPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="dnc-lead"
                      variant="outline"
                      role="combobox"
                      aria-expanded={isLeadPickerOpen}
                      className="w-full justify-between font-normal"
                    >
                      {newLead ? (
                        <span className="truncate">
                          {newLead.name}{" "}
                          <span className="font-mono text-muted-foreground">{newLead.phone_number}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Search a lead by name or number</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    container={addDialogEl}
                    className="w-(--radix-popover-trigger-width) p-0"
                    align="start"
                  >
                    {/* The leads are searched on the server, so cmdk must not filter them again. */}
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Name or phone number..."
                        value={leadQuery}
                        onValueChange={setLeadQuery}
                      />
                      <CommandList>
                        {/* The spinner only until there is something to show: while a new search runs
                            the current results stay, so the list does not flicker on each key. */}
                        {isSearchingLeads && leadResults.length === 0 ? (
                          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching leads...
                          </div>
                        ) : (
                          <>
                            <CommandEmpty>
                              {leadSearchFailed ? "Could not search the leads. Try again." : "No lead found."}
                            </CommandEmpty>
                            <CommandGroup>
                              {leadResults.map((lead) => {
                                const problem = leadProblem(lead);
                                return (
                                  <CommandItem
                                    key={lead.id}
                                    value={lead.id}
                                    disabled={!!problem}
                                    data-dnc-lead={lead.phone_number}
                                    onSelect={() => {
                                      // Agents ticked for another lead must not carry over.
                                      if (newLead?.id !== lead.id) setSelectedAgentIds([]);
                                      setNewLead(lead);
                                      setIsLeadPickerOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4 shrink-0",
                                        newLead?.id === lead.id ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <div className="flex min-w-0 flex-col">
                                      <span className="truncate">{lead.name}</span>
                                      <span className="font-mono text-xs text-muted-foreground">{lead.phone_number}</span>
                                    </div>
                                    {problem && (
                                      <span className="ml-auto pl-2 text-right text-xs text-destructive">{problem}</span>
                                    )}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {newLead && (
                  <p className={`text-xs ${newLeadProblem ? "text-destructive" : "text-muted-foreground"}`}>
                    {newLeadProblem ?? `Will be saved as ${newLead.e164}`}
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label id="dnc-block-for-label">Block for</Label>
                <RadioGroup
                  aria-labelledby="dnc-block-for-label"
                  value={blockFor}
                  onValueChange={(value) => setBlockFor(value as "organization" | "agents")}
                  className="grid gap-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="dnc-block-organization" value="organization" />
                    <Label htmlFor="dnc-block-organization" className="font-normal">
                      Whole organization (every agent, including agents added later)
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="dnc-block-agents" value="agents" />
                    <Label htmlFor="dnc-block-agents" className="font-normal">
                      Selected agents
                    </Label>
                  </div>
                </RadioGroup>
                {blockFor === "agents" && (
                  <div className="overflow-hidden rounded-md border" data-dnc-agents>
                    {/* h-auto: inline here, not in a popover, so it takes its content's height. */}
                    {/* Filter on the agent's name only (its keywords), not on the value, which holds
                        the id: cmdk's fuzzy match would find the typed letters inside every UUID. */}
                    <Command
                      className="h-auto"
                      filter={(_value, search, keywords) =>
                        (keywords ?? []).join(" ").toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
                      }
                    >
                      <CommandInput placeholder="Search agents..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>No agent found.</CommandEmpty>
                        <CommandGroup>
                          {assistants.map((a) => {
                            const alreadyListed = agentsListingNewLead.has(a.id);
                            const ticked = alreadyListed || selectedAgentIds.includes(a.id);
                            return (
                              // value: the id, unique; keywords: the name the search matches.
                              <CommandItem
                                key={a.id}
                                value={a.id}
                                keywords={[a.name]}
                                disabled={alreadyListed}
                                data-dnc-agent={a.id}
                                onSelect={() => toggleAgent(a.id)}
                              >
                                <Checkbox
                                  checked={ticked}
                                  disabled={alreadyListed}
                                  tabIndex={-1}
                                  aria-hidden="true"
                                  className="mr-2 pointer-events-none"
                                />
                                <span className="truncate">{a.name}</span>
                                {alreadyListed && (
                                  <span className="ml-auto pl-2 text-xs text-muted-foreground">Already blocked</span>
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                    <p className="border-t px-3 py-2 text-xs text-muted-foreground" data-dnc-agent-count>
                      {tickableAgentIds.length === 0
                        ? "Tick one or more agents"
                        : `${tickableAgentIds.length} ${tickableAgentIds.length === 1 ? "agent" : "agents"} selected`}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="dnc-reason">Reason (optional)</Label>
                <Input
                  id="dnc-reason"
                  placeholder="e.g., Asked by email to stop calling"
                  maxLength={500}
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  // A plain button closes the dialog without onOpenChange, so reset here too.
                  setIsAddDialogOpen(false);
                  resetAddForm();
                }}
                disabled={isAdding}
              >
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={!canAdd}>
                {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Number
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {numbers.length === 0 ? (
          <div className="text-center py-6">
            <PhoneOff className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <h3 className="text-lg font-medium">No blocked numbers</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Numbers you add here, or that ask on a call to stop, will show up here. Owners,
              admins and super admins can remove a number.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-7 gap-4 py-2 px-4 font-medium text-sm text-muted-foreground">
              <div>Number</div>
              <div>Blocked for</div>
              <div className="col-span-2">Reason</div>
              <div>Source</div>
              <div>Added</div>
              <div className="text-right">Actions</div>
            </div>
            {numbers.map((row) => (
              <div
                key={row.id}
                data-dnc-row={row.phone_number}
                className="grid grid-cols-7 gap-4 py-3 px-4 rounded-lg border items-center"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{row.phone_number}</div>
                  {row.lead_id && (
                    <div className="truncate text-xs text-muted-foreground" title="The lead this number was added from">
                      {/* A deleted lead clears lead_id, so no name here means the viewer cannot see
                          the lead (leads RLS), not that it is gone. */}
                      {row.lead ? leadName(row.lead) : "From a lead"}
                    </div>
                  )}
                </div>
                <div className="text-sm">
                  {row.assistant_id ? (
                    <span title="Only this agent's calls are refused">{row.assistant?.name ?? "One agent"}</span>
                  ) : (
                    <span className="text-muted-foreground">Whole organization</span>
                  )}
                </div>
                <div className="col-span-2 text-sm text-muted-foreground break-words">
                  {row.reason || "—"}
                </div>
                <div className="text-sm">
                  <Badge variant={row.source === "candidate_request" ? "secondary" : "outline"}>
                    {DO_NOT_CALL_SOURCE_LABELS[row.source] ?? row.source}
                  </Badge>
                  {/* Only a number listed from a call has a call behind it to look at; one added by
                      hand or through the API does not. Calls store to_number with or without the
                      '+'; the last 10 digits match both. */}
                  {row.source === "candidate_request" && (
                    <Link
                      to={`/call-logs?search=${row.phone_number.slice(-10)}`}
                      className="block text-xs text-primary hover:underline mt-1"
                    >
                      View calls
                    </Link>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">{formatDate(row.created_at)}</div>
                <div className="flex justify-end">
                  {canRemove && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive" title="Remove">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove from the do-not-call list</AlertDialogTitle>
                        <AlertDialogDescription>
                          {row.phone_number} can be called again by{" "}
                          {row.assistant ? row.assistant.name : "this organization"}. Calls that were
                          cancelled when it was added stay cancelled.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleRemove(row)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
